import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, FileText, Stethoscope, CheckCircle, X, Clock, CurrencyInr, Paperclip, ArrowRight, Eye } from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [manualPayments, setManualPayments] = useState([]);
  const [doctorVerifications, setDoctorVerifications] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [verificationStatusFilter, setVerificationStatusFilter] = useState('pending');
  const [appStatus, setAppStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [caseStatusFilter, setCaseStatusFilter] = useState('all');
  const [selectedCase, setSelectedCase] = useState(null);
  const [doctorsForAssign, setDoctorsForAssign] = useState([]);
  const [assignDoctorId, setAssignDoctorId] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchApplications(appStatus);
    fetchPayouts();
    fetchManualPayments();
    fetchCases();
    fetchDoctorVerifications();
  }, []);

  const fetchDoctorVerifications = async (status = 'pending') => {
    try {
      const { data } = await axios.get(`${API}/admin/doctor-verifications?status=${status}`, { withCredentials: true });
      setDoctorVerifications(data.doctors || []);
    } catch (error) {
      console.error('Failed to fetch doctor verifications', error);
    }
  };

  const handleVerifyDoctor = async (doctorId, action) => {
    let note = '';
    if (action === 'reject') {
      note = window.prompt('Reason for rejection (optional):') || '';
    }
    try {
      await axios.post(
        `${API}/admin/doctor-verifications/${doctorId}/verify`,
        { action, note },
        { withCredentials: true }
      );
      toast.success(`Doctor ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'reset to pending'}`);
      setSelectedDoctor(null);
      fetchDoctorVerifications(verificationStatusFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  useEffect(() => {
    fetchApplications(appStatus);
  }, [appStatus]);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(data);
    } catch (error) {
      toast.error('Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async (status) => {
    try {
      const { data } = await axios.get(`${API}/admin/doctor-applications?status=${status}`, { withCredentials: true });
      setApplications(data.applications || []);
    } catch (error) {
      console.error('Failed to fetch applications');
    }
  };

  const fetchPayouts = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/payouts`, { withCredentials: true });
      setPayouts(data.payouts || []);
    } catch (error) {
      console.error('Failed to fetch payouts');
    }
  };

  const fetchManualPayments = async (status = 'pending_verification') => {    try {
      const { data } = await axios.get(`${API}/admin/manual-payments?status=${status}`, { withCredentials: true });
      setManualPayments(data.payments || []);
    } catch (error) {
      console.error('Failed to fetch manual payments');
    }
  };

  const handleVerifyManualPayment = async (txnId, action) => {
    const note = action === 'reject' ? (window.prompt('Reason for rejection (optional):') || '') : '';
    try {
      await axios.post(
        `${API}/admin/manual-payments/${txnId}/verify`,
        { action, admin_note: note },
        { withCredentials: true }
      );
      toast.success(`Payment ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchManualPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    }
  };

  const fetchCases = async () => {
    try {
      const { data } = await axios.get(`${API}/cases`, { withCredentials: true });
      setCases(data.cases || []);
    } catch (error) {
      console.error('Failed to fetch cases', error);
    }
  };

  const openCaseDetail = async (caseId, specialty) => {
    try {
      const { data } = await axios.get(`${API}/cases/${caseId}`, { withCredentials: true });
      setSelectedCase(data);
      setAssignDoctorId('');
      const docResp = await axios.get(`${API}/admin/doctors?specialty=${encodeURIComponent(specialty || '')}`, { withCredentials: true });
      setDoctorsForAssign(docResp.data.doctors || []);
    } catch (error) {
      toast.error('Failed to load case details');
    }
  };

  const handleAssignDoctor = async () => {
    if (!assignDoctorId) {
      toast.error('Please select a doctor first');
      return;
    }
    setAssigning(true);
    try {
      await axios.post(
        `${API}/cases/${selectedCase.case_id}/assign`,
        { doctor_id: assignDoctorId },
        { withCredentials: true }
      );
      toast.success('Case forwarded to doctor. Email notification sent.');
      setSelectedCase(null);
      fetchCases();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to forward case');
    } finally {
      setAssigning(false);
    }
  };

  const downloadFile = async (filePath, filename) => {
    try {
      const resp = await axios.get(`${API}/files/${filePath}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const filteredCases = caseStatusFilter === 'all'
    ? cases
    : cases.filter(c => c.status === caseStatusFilter);

  const handleApprove = async (id) => {
    try {
      await axios.post(`${API}/admin/doctor-applications/${id}/approve`, {}, { withCredentials: true });
      toast.success('Doctor approved!');
      fetchApplications(appStatus);
      fetchStats();
      fetchPayouts();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.post(`${API}/admin/doctor-applications/${id}/reject`, {}, { withCredentials: true });
      toast.success('Doctor application rejected');
      fetchApplications(appStatus);
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const handleMarkPaid = async (doctorId, amount) => {
    const txnRef = window.prompt(`Mark ₹${amount} as paid. Enter transaction reference (UPI ref, NEFT, etc):`);
    if (!txnRef) return;
    try {
      await axios.post(
        `${API}/admin/payouts/${doctorId}/mark-paid?amount=${amount}&transaction_ref=${encodeURIComponent(txnRef)}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Payout of ₹${amount} recorded`);
      fetchPayouts();
    } catch (error) {
      toast.error('Failed to record payout');
    }
  };

  const exportPayoutsCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Specialty', 'Referral Code', 'Total Referred', 'Paid Consultations', 'Total Earned (₹)', 'Total Paid Out (₹)', 'Pending Balance (₹)'];
    const rows = payouts.map(p => [p.name, p.email, p.phone, p.specialty, p.referral_code, p.total_referred, p.paid_consultations, p.total_earned, p.total_paid_out, p.pending_balance]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payouts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="admin-dashboard">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-manrope text-3xl font-bold text-primary mb-2">Admin Dashboard</h1>
              <p className="text-secondary">Welcome, {user?.name}</p>
            </div>
            <Link
              to="/admin/blog"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-full font-medium hover:bg-primary/90 text-sm"
              data-testid="manage-blog-button"
            >
              <FileText size={18} weight="bold" /> Manage Blog
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div className="card" data-testid="stat-total-cases">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-secondary mb-1">Total Cases</p>
                      <p className="text-3xl font-bold text-primary">{stats?.total_cases || 0}</p>
                    </div>
                    <FileText size={40} weight="duotone" className="text-primary" />
                  </div>
                </div>
                <div className="card" data-testid="stat-total-patients">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-secondary mb-1">Total Patients</p>
                      <p className="text-3xl font-bold text-primary">{stats?.total_patients || 0}</p>
                    </div>
                    <Users size={40} weight="duotone" className="text-primary" />
                  </div>
                </div>
                <div className="card" data-testid="stat-total-doctors">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-secondary mb-1">Active Doctors</p>
                      <p className="text-3xl font-bold text-primary">{stats?.total_doctors || 0}</p>
                    </div>
                    <Stethoscope size={40} weight="duotone" className="text-primary" />
                  </div>
                </div>
              </div>

              {/* Cases - Submitted by Patients */}
              <div className="card mb-8" data-testid="admin-cases-panel">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <h2 className="font-manrope text-xl font-semibold text-primary flex items-center gap-2">
                    <FileText size={24} weight="duotone" />
                    Patient Cases ({filteredCases.length})
                  </h2>
                  <div className="flex gap-2 flex-wrap">
                    {['all', 'submitted', 'under_review', 'opinion_ready'].map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setCaseStatusFilter(s)}
                        className={`px-3 py-1 text-xs rounded-full border ${
                          caseStatusFilter === s
                            ? 'bg-primary text-white border-primary'
                            : 'border-primary/30 text-primary hover:bg-primary/5'
                        }`}
                        data-testid={`case-filter-${s}`}
                      >
                        {s === 'all' ? 'All' : s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </button>
                    ))}
                  </div>
                </div>
                {filteredCases.length === 0 ? (
                  <p className="text-secondary text-sm py-4" data-testid="no-cases">No cases in this filter.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-primary/20 text-secondary">
                          <th className="text-left py-2 px-3 font-medium">Patient</th>
                          <th className="text-left py-2 px-3 font-medium">Specialty</th>
                          <th className="text-left py-2 px-3 font-medium">Status</th>
                          <th className="text-left py-2 px-3 font-medium">Assigned to</th>
                          <th className="text-left py-2 px-3 font-medium">Submitted</th>
                          <th className="text-right py-2 px-3 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCases.map((c, idx) => (
                          <tr key={c.case_id} className="border-b border-primary/10" data-testid={`admin-case-row-${idx}`}>
                            <td className="py-3 px-3">
                              <div className="font-medium text-primary">{c.patient_name || '—'}</div>
                              <div className="text-xs text-secondary">{c.patient_email}</div>
                            </td>
                            <td className="py-3 px-3 text-secondary">
                              <div>{c.specialty}</div>
                              {c.subspecialty && <div className="text-xs">{c.subspecialty}</div>}
                            </td>
                            <td className="py-3 px-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                c.status === 'submitted' ? 'bg-amber-100 text-amber-800' :
                                c.status === 'under_review' ? 'bg-blue-100 text-blue-800' :
                                c.status === 'opinion_ready' ? 'bg-green-100 text-green-800' :
                                'bg-secondary/10 text-secondary'
                              }`}>
                                {c.status?.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-secondary">
                              {c.doctor_name || <span className="italic text-xs">Not assigned</span>}
                            </td>
                            <td className="py-3 px-3 text-xs text-secondary">
                              {c.created_at?.split('T')[0]}
                            </td>
                            <td className="py-3 px-3 text-right">
                              <button
                                onClick={() => openCaseDetail(c.case_id, c.specialty)}
                                className="inline-flex items-center gap-1 px-3 py-1 text-xs bg-primary text-white rounded-full hover:bg-primary/90"
                                data-testid={`view-case-${idx}`}
                              >
                                <Eye size={14} weight="bold" /> Review
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Doctor Applications */}
              <div className="card mb-8">
                <h2 className="font-manrope text-xl font-semibold text-primary mb-4 flex items-center gap-2">
                  <Stethoscope size={24} weight="duotone" />
                  Doctor Applications
                </h2>
                <Tabs defaultValue="pending" onValueChange={setAppStatus}>
                  <TabsList className="mb-4">
                    <TabsTrigger value="pending" data-testid="tab-pending-apps">Pending</TabsTrigger>
                    <TabsTrigger value="active" data-testid="tab-approved-apps">Approved</TabsTrigger>
                    <TabsTrigger value="rejected" data-testid="tab-rejected-apps">Rejected</TabsTrigger>
                  </TabsList>

                  <TabsContent value={appStatus}>
                    {applications.length === 0 ? (
                      <div className="text-center py-8 text-secondary" data-testid="no-applications">
                        <Clock size={48} weight="duotone" className="mx-auto mb-3 opacity-50" />
                        No {appStatus} applications
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {applications.map((app, idx) => (
                          <div
                            key={app.id}
                            className="border border-primary/20 rounded-lg p-4"
                            data-testid={`application-${idx}`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <p className="font-semibold text-primary">{app.name}</p>
                                <p className="text-sm text-secondary">
                                  {app.specialty} · {app.years_experience}+ years · {app.email}
                                </p>
                                <p className="text-xs text-secondary mt-1">
                                  {app.qualifications} · Reg: {app.registration_number}
                                  {app.current_hospital && ` · ${app.current_hospital}`}
                                </p>
                                {app.bio && <p className="text-xs text-secondary mt-2 italic">"{app.bio}"</p>}
                              </div>
                              {appStatus === 'pending' && (
                                <div className="flex gap-2 flex-shrink-0">
                                  <button
                                    onClick={() => handleApprove(app.id)}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-full text-xs font-medium hover:bg-primary/90"
                                    data-testid={`approve-button-${idx}`}
                                  >
                                    <CheckCircle size={14} weight="bold" />
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => handleReject(app.id)}
                                    className="flex items-center gap-1 px-3 py-1.5 border border-red-300 text-red-600 rounded-full text-xs font-medium hover:bg-red-50"
                                    data-testid={`reject-button-${idx}`}
                                  >
                                    <X size={14} weight="bold" />
                                    Reject
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>

              {/* Manual UPI Payment Verifications */}
              <div className="card mb-8" data-testid="manual-payments-panel">
                <h2 className="font-manrope text-xl font-semibold text-primary mb-4 flex items-center gap-2">
                  <CurrencyInr size={24} weight="duotone" />
                  Manual UPI Payments — Pending Verification
                  {manualPayments.length > 0 && (
                    <span className="text-xs bg-accent text-white rounded-full px-2 py-0.5">{manualPayments.length}</span>
                  )}
                </h2>
                {manualPayments.length === 0 ? (
                  <p className="text-secondary text-sm py-4">No manual payments awaiting verification.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-primary/20 text-secondary">
                          <th className="text-left py-2 px-3 font-medium">Patient</th>
                          <th className="text-left py-2 px-3 font-medium">Purpose</th>
                          <th className="text-left py-2 px-3 font-medium">UPI Txn ID</th>
                          <th className="text-right py-2 px-3 font-medium">Amount</th>
                          <th className="text-left py-2 px-3 font-medium">Submitted</th>
                          <th className="text-right py-2 px-3 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {manualPayments.map((p, idx) => (
                          <tr key={p.id} className="border-b border-primary/10" data-testid={`manual-payment-row-${idx}`}>
                            <td className="py-3 px-3 text-primary">{p.email}</td>
                            <td className="py-3 px-3 text-secondary capitalize">
                              {p.purpose?.replace('_', ' ')}
                              {p.package_id && ` · ${p.package_id}`}
                            </td>
                            <td className="py-3 px-3 font-mono text-xs text-primary">{p.manual_transaction_id}</td>
                            <td className="py-3 px-3 text-right font-semibold text-primary">₹{Number(p.amount).toLocaleString('en-IN')}</td>
                            <td className="py-3 px-3 text-xs text-secondary">{p.created_at?.split('T')[0]}</td>
                            <td className="py-3 px-3 text-right space-x-1">
                              <button
                                onClick={() => handleVerifyManualPayment(p.id, 'approve')}
                                className="px-3 py-1 text-xs bg-primary text-white rounded-full hover:bg-primary/90"
                                data-testid={`approve-manual-payment-${idx}`}
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleVerifyManualPayment(p.id, 'reject')}
                                className="px-3 py-1 text-xs border border-red-300 text-red-600 rounded-full hover:bg-red-50"
                                data-testid={`reject-manual-payment-${idx}`}
                              >
                                Reject
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Doctor Verification — empanelment review */}
              <div className="card mb-8" data-testid="doctor-verification-panel">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <h2 className="font-manrope text-xl font-semibold text-primary flex items-center gap-2">
                    <Stethoscope size={24} weight="duotone" />
                    Doctor Empanelment &amp; Verification ({doctorVerifications.length})
                  </h2>
                  <div className="flex gap-2 flex-wrap">
                    {['pending', 'active', 'rejected'].map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => { setVerificationStatusFilter(s); fetchDoctorVerifications(s); }}
                        className={`px-3 py-1 text-xs rounded-full border ${
                          verificationStatusFilter === s
                            ? 'bg-primary text-white border-primary'
                            : 'border-primary/30 text-primary hover:bg-primary/5'
                        }`}
                        data-testid={`verification-filter-${s}`}
                      >
                        {s === 'pending' ? 'Pending Review' : s === 'active' ? 'Approved' : 'Rejected'}
                      </button>
                    ))}
                  </div>
                </div>
                {doctorVerifications.length === 0 ? (
                  <p className="text-secondary text-sm py-4" data-testid="no-doctor-verifications">
                    No doctors with status "{verificationStatusFilter}".
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-primary/20 text-secondary">
                          <th className="text-left py-2 px-3 font-medium">Doctor</th>
                          <th className="text-left py-2 px-3 font-medium">Specialty</th>
                          <th className="text-left py-2 px-3 font-medium">Certificates</th>
                          <th className="text-left py-2 px-3 font-medium">Registered</th>
                          <th className="text-right py-2 px-3 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {doctorVerifications.map((d, idx) => (
                          <tr key={d.id} className="border-b border-primary/10" data-testid={`verification-row-${idx}`}>
                            <td className="py-3 px-3">
                              <div className="font-medium text-primary">{d.name}</div>
                              <div className="text-xs text-secondary">{d.email}</div>
                              {d.phone && <div className="text-xs text-secondary">{d.phone}</div>}
                            </td>
                            <td className="py-3 px-3 text-secondary">
                              <div>{d.specialty || '—'}</div>
                              {d.subspecialty && <div className="text-xs">{d.subspecialty}</div>}
                            </td>
                            <td className="py-3 px-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                (d.certificates?.length || 0) > 0 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                              }`}>
                                {d.certificates?.length || 0} file{(d.certificates?.length || 0) !== 1 ? 's' : ''}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-xs text-secondary">{d.created_at?.split('T')[0]}</td>
                            <td className="py-3 px-3 text-right">
                              <button
                                onClick={() => setSelectedDoctor(d)}
                                className="inline-flex items-center gap-1 px-3 py-1 text-xs bg-primary text-white rounded-full hover:bg-primary/90"
                                data-testid={`review-doctor-${idx}`}
                              >
                                <Eye size={14} weight="bold" /> Review
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Doctor Payouts */}
              <div className="card mb-8" data-testid="payouts-panel">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h2 className="font-manrope text-xl font-semibold text-primary flex items-center gap-2">
                    <CurrencyInr size={24} weight="duotone" />
                    Doctor Referral Payouts
                  </h2>
                  <button
                    onClick={exportPayoutsCSV}
                    className="px-4 py-2 text-sm font-medium border-2 border-primary text-primary rounded-full hover:bg-primary hover:text-white transition-all"
                    data-testid="export-payouts-csv"
                  >
                    Export CSV
                  </button>
                </div>

                {payouts.length === 0 ? (
                  <p className="text-secondary text-sm py-4">No doctors with referrals yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-primary/20 text-secondary">
                          <th className="text-left py-2 px-3 font-medium">Doctor</th>
                          <th className="text-left py-2 px-3 font-medium">Code</th>
                          <th className="text-right py-2 px-3 font-medium">Paid Consults</th>
                          <th className="text-right py-2 px-3 font-medium">Earned</th>
                          <th className="text-right py-2 px-3 font-medium">Paid Out</th>
                          <th className="text-right py-2 px-3 font-medium">Pending</th>
                          <th className="text-right py-2 px-3 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payouts.map((p, idx) => (
                          <tr key={p.doctor_id} className="border-b border-primary/10" data-testid={`payout-row-${idx}`}>
                            <td className="py-3 px-3">
                              <p className="font-semibold text-primary">{p.name}</p>
                              <p className="text-xs text-secondary">{p.specialty}</p>
                            </td>
                            <td className="py-3 px-3 font-mono text-xs text-secondary">{p.referral_code}</td>
                            <td className="py-3 px-3 text-right text-primary">{p.paid_consultations}</td>
                            <td className="py-3 px-3 text-right text-primary">₹{p.total_earned.toLocaleString('en-IN')}</td>
                            <td className="py-3 px-3 text-right text-secondary">₹{p.total_paid_out.toLocaleString('en-IN')}</td>
                            <td className="py-3 px-3 text-right">
                              <span className={`font-semibold ${p.pending_balance > 0 ? 'text-accent' : 'text-secondary'}`}>
                                ₹{p.pending_balance.toLocaleString('en-IN')}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-right">
                              {p.pending_balance > 0 ? (
                                <button
                                  onClick={() => handleMarkPaid(p.doctor_id, p.pending_balance)}
                                  className="px-3 py-1 text-xs bg-primary text-white rounded-full hover:bg-primary/90"
                                  data-testid={`mark-paid-button-${idx}`}
                                >
                                  Mark Paid
                                </button>
                              ) : (
                                <span className="text-xs text-secondary">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Cases by Status & Specialty */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="card">
                  <h2 className="font-manrope text-xl font-semibold text-primary mb-4">Cases by Status</h2>
                  <div className="space-y-3">
                    {stats?.cases_by_status?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-surface-secondary rounded-lg">
                        <span className="text-secondary capitalize">{item._id?.replace('_', ' ') || 'Unknown'}</span>
                        <span className="font-semibold text-primary">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card">
                  <h2 className="font-manrope text-xl font-semibold text-primary mb-4">Cases by Specialty</h2>
                  <div className="space-y-3">
                    {stats?.cases_by_specialty?.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-surface-secondary rounded-lg">
                        <span className="text-secondary">{item._id || 'Unknown'}</span>
                        <span className="font-semibold text-primary">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Doctor Verification Modal */}
      {selectedDoctor && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedDoctor(null)}
          data-testid="doctor-verification-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-primary/10 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-manrope text-xl font-semibold text-primary">Doctor Verification</h3>
                <p className="text-xs text-secondary mt-1">{selectedDoctor.name} · {selectedDoctor.specialty || '—'}</p>
              </div>
              <button
                onClick={() => setSelectedDoctor(null)}
                className="p-2 hover:bg-primary/10 rounded-full"
                data-testid="close-doctor-verification"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Identity */}
              <section className="bg-surface-secondary rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between gap-3"><span className="text-secondary">Name:</span><span className="text-primary font-medium text-right">{selectedDoctor.name}</span></div>
                <div className="flex justify-between gap-3"><span className="text-secondary">Email:</span><span className="text-primary text-right break-all">{selectedDoctor.email}</span></div>
                {selectedDoctor.phone && <div className="flex justify-between gap-3"><span className="text-secondary">Phone / WhatsApp:</span><span className="text-primary text-right">{selectedDoctor.phone}</span></div>}
                <div className="flex justify-between gap-3"><span className="text-secondary">Specialty:</span><span className="text-primary text-right">{selectedDoctor.specialty || '—'}</span></div>
                {selectedDoctor.subspecialty && <div className="flex justify-between gap-3"><span className="text-secondary">Subspecialty:</span><span className="text-primary text-right">{selectedDoctor.subspecialty}</span></div>}
                {selectedDoctor.qualifications && <div className="flex justify-between gap-3"><span className="text-secondary">Qualifications:</span><span className="text-primary text-right">{selectedDoctor.qualifications}</span></div>}
                {selectedDoctor.credentials && <div className="flex justify-between gap-3"><span className="text-secondary">Credentials:</span><span className="text-primary text-right">{selectedDoctor.credentials}</span></div>}
                {selectedDoctor.registration_number && <div className="flex justify-between gap-3"><span className="text-secondary">MCI Reg:</span><span className="text-primary text-right font-mono">{selectedDoctor.registration_number}</span></div>}
                {selectedDoctor.years_experience && <div className="flex justify-between gap-3"><span className="text-secondary">Experience:</span><span className="text-primary text-right">{selectedDoctor.years_experience} years</span></div>}
                {selectedDoctor.current_hospital && <div className="flex justify-between gap-3"><span className="text-secondary">Current Hospital:</span><span className="text-primary text-right">{selectedDoctor.current_hospital}</span></div>}
              </section>

              {selectedDoctor.bio && (
                <section>
                  <h4 className="text-sm uppercase tracking-wide text-secondary font-semibold mb-2">About</h4>
                  <p className="text-sm text-primary whitespace-pre-wrap leading-relaxed">{selectedDoctor.bio}</p>
                </section>
              )}

              {/* Certificates */}
              <section>
                <h4 className="text-sm uppercase tracking-wide text-secondary font-semibold mb-2 flex items-center gap-2">
                  <Paperclip size={14} weight="bold" /> Uploaded Certificates ({selectedDoctor.certificates?.length || 0})
                </h4>
                {(selectedDoctor.certificates?.length || 0) === 0 ? (
                  <p className="text-sm text-secondary italic">No certificates uploaded. Doctor did not attach any documents during sign-up.</p>
                ) : (
                  <div className="space-y-2">
                    {selectedDoctor.certificates.map((f, idx) => (
                      <button
                        key={f.file_id || idx}
                        onClick={() => downloadFile(f.storage_path, f.original_filename)}
                        className="w-full flex items-center justify-between p-3 bg-surface-secondary rounded-lg hover:bg-primary/5 transition-colors text-left"
                        data-testid={`download-cert-${idx}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Paperclip size={18} weight="bold" className="text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-primary truncate font-medium">{f.original_filename}</p>
                            <p className="text-xs text-secondary">{f.size ? `${(f.size / 1024).toFixed(0)} KB` : ''} {f.content_type || ''}</p>
                          </div>
                        </div>
                        <ArrowRight size={16} weight="bold" className="text-accent flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </section>

              {/* Status */}
              <section className="border-t border-primary/10 pt-4">
                <p className="text-sm text-secondary mb-1">Current status:</p>
                <span className={`inline-block px-3 py-1 text-xs rounded-full ${
                  selectedDoctor.status === 'active' ? 'bg-green-100 text-green-800' :
                  selectedDoctor.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-amber-100 text-amber-800'
                }`}>
                  {selectedDoctor.status === 'active' ? 'Approved' : selectedDoctor.status === 'rejected' ? 'Rejected' : 'Pending Review'}
                </span>
                {selectedDoctor.verification_note && (
                  <p className="text-xs text-secondary mt-2 italic">Note: {selectedDoctor.verification_note}</p>
                )}
                {selectedDoctor.verified_by && (
                  <p className="text-xs text-secondary mt-1">By {selectedDoctor.verified_by} on {selectedDoctor.verified_at?.split('T')[0]}</p>
                )}
              </section>

              {/* Actions */}
              <section className="flex flex-wrap gap-2">
                {selectedDoctor.status !== 'active' && (
                  <button
                    onClick={() => handleVerifyDoctor(selectedDoctor.id, 'approve')}
                    className="flex-1 min-w-[140px] px-4 py-2.5 bg-primary text-white rounded-full font-medium hover:bg-primary/90"
                    data-testid="approve-doctor-button"
                  >
                    Approve &amp; Activate
                  </button>
                )}
                {selectedDoctor.status !== 'rejected' && (
                  <button
                    onClick={() => handleVerifyDoctor(selectedDoctor.id, 'reject')}
                    className="flex-1 min-w-[140px] px-4 py-2.5 border-2 border-red-300 text-red-600 rounded-full font-medium hover:bg-red-50"
                    data-testid="reject-doctor-button"
                  >
                    Reject
                  </button>
                )}
                {selectedDoctor.status !== 'pending' && (
                  <button
                    onClick={() => handleVerifyDoctor(selectedDoctor.id, 'reset')}
                    className="px-4 py-2.5 border border-primary/30 text-primary rounded-full font-medium hover:bg-primary/5 text-sm"
                    data-testid="reset-doctor-button"
                  >
                    Reset to Pending
                  </button>
                )}
              </section>
            </div>
          </div>
        </div>
      )}

      {/* Case Detail Modal */}
      {selectedCase && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedCase(null)}
          data-testid="case-detail-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-primary/10 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-manrope text-xl font-semibold text-primary">
                  Case #{selectedCase.case_id?.slice(-8).toUpperCase()}
                </h3>
                <p className="text-xs text-secondary mt-1">
                  Submitted {selectedCase.created_at?.split('T')[0]} · {selectedCase.tier?.toUpperCase()} tier
                </p>
              </div>
              <button
                onClick={() => setSelectedCase(null)}
                className="p-2 hover:bg-primary/10 rounded-full"
                data-testid="close-case-detail"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Patient Info */}
              <section>
                <h4 className="text-sm uppercase tracking-wide text-secondary font-semibold mb-2">Patient</h4>
                <div className="bg-surface-secondary rounded-lg p-4 text-sm space-y-1">
                  <p><strong className="text-primary">{selectedCase.patient_name}</strong></p>
                  <p className="text-secondary">{selectedCase.patient_email}</p>
                </div>
              </section>

              {/* Specialty */}
              <section>
                <h4 className="text-sm uppercase tracking-wide text-secondary font-semibold mb-2">Specialty</h4>
                <p className="text-primary">
                  {selectedCase.specialty}
                  {selectedCase.subspecialty && <span className="text-secondary"> · {selectedCase.subspecialty}</span>}
                </p>
              </section>

              {/* Medical Info */}
              <section className="space-y-3">
                <h4 className="text-sm uppercase tracking-wide text-secondary font-semibold">Medical Information</h4>
                <div>
                  <p className="text-xs text-secondary mb-1">Symptoms</p>
                  <p className="text-primary text-sm whitespace-pre-wrap">{selectedCase.symptoms || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-secondary mb-1">Duration</p>
                  <p className="text-primary text-sm">{selectedCase.duration || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-secondary mb-1">Current Diagnosis</p>
                  <p className="text-primary text-sm whitespace-pre-wrap">{selectedCase.current_diagnosis || '—'}</p>
                </div>
                {selectedCase.medications && (
                  <div>
                    <p className="text-xs text-secondary mb-1">Medications</p>
                    <p className="text-primary text-sm whitespace-pre-wrap">{selectedCase.medications}</p>
                  </div>
                )}
                {selectedCase.allergies && (
                  <div>
                    <p className="text-xs text-secondary mb-1">Allergies</p>
                    <p className="text-primary text-sm">{selectedCase.allergies}</p>
                  </div>
                )}
                {selectedCase.medical_history && (
                  <div>
                    <p className="text-xs text-secondary mb-1">Medical History</p>
                    <p className="text-primary text-sm whitespace-pre-wrap">{selectedCase.medical_history}</p>
                  </div>
                )}
              </section>

              {/* Files */}
              {selectedCase.files?.length > 0 && (
                <section>
                  <h4 className="text-sm uppercase tracking-wide text-secondary font-semibold mb-2 flex items-center gap-2">
                    <Paperclip size={16} weight="bold" /> Uploaded Files ({selectedCase.files.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedCase.files.map((f, idx) => (
                      <button
                        key={idx}
                        onClick={() => downloadFile(f.storage_path, f.original_filename)}
                        className="w-full flex items-center justify-between p-3 bg-surface-secondary rounded-lg hover:bg-primary/5 transition-colors text-left"
                        data-testid={`download-file-${idx}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-primary truncate">{f.original_filename}</p>
                          <p className="text-xs text-secondary">{(f.size / 1024).toFixed(1)} KB · {f.content_type}</p>
                        </div>
                        <ArrowRight size={16} weight="bold" className="text-primary flex-shrink-0 ml-3" />
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* AI Analysis */}
              {selectedCase.ai_analysis && (
                <section>
                  <h4 className="text-sm uppercase tracking-wide text-secondary font-semibold mb-2">AI Preliminary Analysis</h4>
                  <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 text-sm text-primary whitespace-pre-wrap max-h-64 overflow-y-auto">
                    {selectedCase.ai_analysis}
                  </div>
                </section>
              )}

              {/* Doctor Opinion (if ready) */}
              {selectedCase.opinion && (
                <section>
                  <h4 className="text-sm uppercase tracking-wide text-secondary font-semibold mb-2">Doctor Opinion</h4>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm space-y-3">
                    <p className="font-semibold text-primary">By Dr. {selectedCase.opinion.doctor_name}</p>
                    {selectedCase.opinion.diagnosis_confirmation && <div><strong>Diagnosis:</strong> <span className="whitespace-pre-wrap">{selectedCase.opinion.diagnosis_confirmation}</span></div>}
                    {selectedCase.opinion.alternative_treatments && <div><strong>Treatment:</strong> <span className="whitespace-pre-wrap">{selectedCase.opinion.alternative_treatments}</span></div>}
                    {selectedCase.opinion.surgery_necessity && <div><strong>Surgery Needed:</strong> {selectedCase.opinion.surgery_necessity}</div>}
                    {selectedCase.opinion.risk_benefit_analysis && <div><strong>Risk-Benefit:</strong> <span className="whitespace-pre-wrap">{selectedCase.opinion.risk_benefit_analysis}</span></div>}
                    {selectedCase.opinion.prognosis && <div><strong>Prognosis:</strong> <span className="whitespace-pre-wrap">{selectedCase.opinion.prognosis}</span></div>}
                    {selectedCase.opinion.follow_up_recommendations && <div><strong>Follow-up:</strong> <span className="whitespace-pre-wrap">{selectedCase.opinion.follow_up_recommendations}</span></div>}
                    {selectedCase.opinion.files?.length > 0 && (
                      <div className="border-t border-green-200 pt-3 mt-3">
                        <p className="font-semibold mb-2 flex items-center gap-1"><Paperclip size={14} weight="bold" /> Attached Documents ({selectedCase.opinion.files.length})</p>
                        <div className="space-y-2">
                          {selectedCase.opinion.files.map((f, idx) => (
                            <button
                              key={idx}
                              onClick={() => downloadFile(f.storage_path, f.original_filename)}
                              className="w-full flex items-center justify-between p-2 bg-white rounded-lg hover:bg-primary/5 transition-colors text-left"
                              data-testid={`admin-download-opinion-file-${idx}`}
                            >
                              <span className="text-xs text-primary truncate">{f.original_filename}</span>
                              <ArrowRight size={14} weight="bold" className="text-primary flex-shrink-0 ml-2" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Forward to Doctor */}
              {!selectedCase.doctor_id && (
                <section className="border-t border-primary/10 pt-6">
                  <h4 className="text-sm uppercase tracking-wide text-accent font-semibold mb-3">Forward to Specialist</h4>
                  <div className="space-y-3">
                    <select
                      value={assignDoctorId}
                      onChange={(e) => setAssignDoctorId(e.target.value)}
                      className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-primary"
                      data-testid="assign-doctor-select"
                    >
                      <option value="">
                        {doctorsForAssign.length === 0
                          ? `No ${selectedCase.specialty} doctors available`
                          : `Select a ${selectedCase.specialty} specialist...`}
                      </option>
                      {doctorsForAssign.map(d => (
                        <option key={d.id} value={d.id}>
                          Dr. {d.name} — {d.specialty} ({d.email})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleAssignDoctor}
                      disabled={!assignDoctorId || assigning}
                      className="w-full px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="forward-case-button"
                    >
                      {assigning ? 'Forwarding...' : 'Forward Case & Send Email Notification'}
                    </button>
                  </div>
                </section>
              )}

              {selectedCase.doctor_id && (
                <section className="border-t border-primary/10 pt-4">
                  <p className="text-sm text-secondary">
                    Already assigned to <strong className="text-primary">Dr. {selectedCase.doctor_name}</strong>
                  </p>
                </section>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}